import React, { useState } from 'react'
import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import { IconCrown } from '../../../../shared/components/icons/IconCrown'
import { BpTooltip } from '../../../../shared/components/cards/tooltip/BpTooltip'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { BpTable } from '../../../../shared/components/cards/table/BpTable'
import { ColumnsType } from 'antd/lib/table'
import { IconButton } from '../../../../shared/components/buttons/icon-button/IconButton'
import { TriangleButton } from '../../../../shared/components/buttons/triangle/TriangleButton'
import { Square } from '../../../../shared/components/utility/square/Square'
import { usePostUserGetStripePortalLinkMutation } from '../../../../app/api'
import { selectTariffDetails } from '../../../../app/store/reducers/AuthSlice'
import { useTranslation } from 'react-i18next'
import { setModal } from '../../../../app/store/reducers/AppSlice'
import { ChangeTariff } from '../../../../shared/components/utility/change-tariff/ChangeTariff'
import styles from './TariffDetailsModal.module.scss'

const StripeButton = () => {
  const { t } = useTranslation()
  const tariffDetails = useAppSelector(selectTariffDetails)
  const text = React.useMemo(() => {
    const tariff = tariffDetails?.tariff
    if (!tariff || tariff.type === 'trial') return ''

    if (tariff.subscription_status !== 'suspended' && tariff.payment_method === 'stripe')
      return t('pages.tariff.buttons.managePlan')

    if (tariff.subscription_status === 'grace_period' && tariff.type === 'custom')
      return 'Activate and Pay'

    if (tariff.is_subscription_expired) return 'Renew Plan'

    return ''
  }, [tariffDetails?.tariff])

  const [stripePortal, { isLoading }] = usePostUserGetStripePortalLinkMutation()

  if (!text) {
    return null
  }

  return (
    <BpButton
      className={styles.stripeButton}
      loading={isLoading}
      theme={'outline'}
      type='button'
      onClick={() => {
        stripePortal()
          .unwrap()
          .then((response) => {
            if (response.data?.url) window.location.href = response.data.url
          })
          .catch(() => undefined)
      }}
    >
      {text}
    </BpButton>
  )
}

export const TariffDetailsModal = () => {
  const { t } = useTranslation()
  const tariffDetails = useAppSelector(selectTariffDetails)
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
  const dispatch = useAppDispatch()

  const columns: ColumnsType<any> = [
    {
      dataIndex: 'project_name',
      key: 'project_name',
      width: 250,
    },
    {
      title: t('table.columns.keywords'),
      dataIndex: 'keywords_count',
      key: 'keywords_count',
      render: (count: number) => {
        return <div className={styles.keywordsCell}>{count}</div>
      },
    },
  ]

  return (
    <div className={styles.root}>
      <div className={styles.inner}>
        <div className={styles.header}>
          <h3 className={styles.title}>
            {tariffDetails?.tariff?.type === 'trial'
              ? t('pages.tariff.freeTrialPlanName', {
                  count: tariffDetails.tariff?.duration,
                  name: tariffDetails?.tariff?.name,
                })
              : `${tariffDetails?.tariff?.name} ${tariffDetails?.tariff?.currency_symbol}${
                  tariffDetails?.tariff?.price
                } ${t(`pages.tariff.${tariffDetails.tariff?.payment_periodicity}`)}`}
          </h3>
          <div className={styles.badge}>
            <div className={styles.badgeIcon}>
              <IconCrown color={'#A059FF'} />
            </div>
            <p className={styles.badgeContent}>
              <div
                dangerouslySetInnerHTML={{
                  __html: t('pages.tariff.usedCountOfTotalKeywords', {
                    count: tariffDetails?.tariff?.used_keywords_count,
                    total: tariffDetails?.tariff?.max_keywords,
                  }),
                }}
              />
              {tariffDetails?.tariff?.stripe_cancel_at ? (
                <span className={styles.badgeDate}>
                  {t('pages.tariff.expiredDate')} {tariffDetails?.tariff?.stripe_cancel_at}
                </span>
              ) : (
                <span className={styles.badgeDate}>
                  {tariffDetails?.tariff?.type === 'trial'
                    ? t('pages.tariff.expiredDate')
                    : t('pages.tariff.nextPayment')}{' '}
                  {tariffDetails?.tariff?.subscription_expiration_date}
                </span>
              )}
            </p>
            <div className={styles.badgeTooltip}>
              <BpTooltip color={'#A059FF'} title={t('pages.tariff.keywordsTooltip')} />
            </div>
          </div>
        </div>
        <div className={styles.tableWrap}>
          <BpTable
            expandable={{
              expandedRowRender: (record) => (
                <Square depth={'100'}>
                  <div className={styles.expandedContent}>
                    {Object.keys(record.locations).map((key, index) => (
                      <div key={index} className={styles.expandedRow}>
                        <span>{key}</span>
                        {record.locations[key]}
                      </div>
                    ))}
                  </div>
                </Square>
              ),
              expandIcon: ({ expanded, onExpand, record }) => (
                <IconButton onClick={(e) => onExpand(record, e)}>
                  <TriangleButton color='#A059FF' position={expanded ? 'top' : 'down'} />
                </IconButton>
              ),
              onExpand: (expanded: boolean, record: any) => {
                expanded
                  ? setExpandedRowKeys([record.id])
                  : setExpandedRowKeys(expandedRowKeys.filter((project) => project !== record.id))
              },
              expandedRowKeys: expandedRowKeys,
            }}
            columns={columns}
            dataSource={tariffDetails?.project_details}
            hidePagination={true}
            rowKey={(record) => record.id}
            scroll={{ x: 0 }}
            showHeader={!!tariffDetails?.project_details?.length}
            size={'small'}
          />
        </div>
      </div>
      <div className={styles.footer}>
        <div>
          <p className={styles.footerHint}>{t('pages.tariff.moreKeywords')}</p>
          <BpButton
            className={styles.changePlanButton}
            type='button'
            onClick={() =>
              dispatch(setModal({ size: 'full', children: <ChangeTariff />, withBackButton: true }))
            }
          >
            {t('pages.tariff.buttons.changePlan')}
          </BpButton>
        </div>
        <StripeButton />
      </div>
    </div>
  )
}
